/** Tier 2 — all derivable from the same Snapshot, no new extraction (CLAUDE.md §10). */
import { useMemo, useState } from "react";

import { Card, Empty, Money, Row, Rows, Scroller } from "../components/ui.js";
import {
  deriveBalanceSheet,
  deriveCashAccounts,
  deriveLedger,
  deriveProfitLoss,
  deriveRatios,
  deriveTrialBalance,
  formatDate,
  formatMoney,
} from "../derive/reports.js";
import type { ReportLine } from "../derive/reports.js";
import { OpposingBar, RankedBars, RatioBar } from "../components/charts.js";
import { ShareBar } from "../components/ShareBar.js";
import { navigate } from "../routes.js";
import { viewLimits } from "../view.js";
import { useApp } from "../state.js";

export function ReportsIndex() {
  const { snapshot, groups, asOf } = useApp();
  const limits = viewLimits(snapshot);

  // A glance before the list: the two figures every one of these reports is
  // ultimately about, and the one ratio with a conventional pass mark.
  const summary = useMemo(() => {
    if (!snapshot || !groups) return null;
    const sheet = deriveBalanceSheet(snapshot, groups);
    const pl = deriveProfitLoss(snapshot, groups);
    const current = deriveRatios(snapshot, asOf, groups).find((r) => r.name === "Current ratio");
    return { sheet, pl, current };
  }, [snapshot, groups, asOf]);

  const items: Array<{ id: Parameters<typeof navigate>[0]["id"]; title: string; sub: string }> = [
    { id: "balance-sheet", title: "Balance Sheet", sub: "assets and liabilities" },
    { id: "profit-loss", title: "Profit & Loss", sub: "income and expenses" },
    { id: "trial-balance", title: "Trial Balance", sub: "every ledger, both columns" },
    { id: "cash-book", title: "Cash & Bank Book", sub: "movements on cash and bank" },
    { id: "ratios", title: "Ratio Analysis", sub: "liquidity and margins" },
  ];

  return (
    <div className="space-y-4">
      {summary && !limits.hidden.has("balance-sheet") && (
        <Card title="At a glance">
          <OpposingBar
            left={summary.sheet.totalAssets}
            right={summary.sheet.totalLiabilities}
            leftLabel="Assets"
            rightLabel="Liabilities"
          />

          <div className="mt-4 grid grid-cols-2 gap-4">
            <div>
              <div className="text-[11px] font-bold uppercase tracking-[0.08em] text-ink-3">
                Net profit
              </div>
              <div className="tabular mt-1 text-[20px] font-semibold text-ink">
                {formatMoney(summary.pl.netProfit)}
              </div>
            </div>
            {summary.current && (
              <div>
                <div className="mb-1 text-[11px] font-bold uppercase tracking-[0.08em] text-ink-3">
                  Current ratio
                </div>
                <RatioBar
                  value={summary.current.value}
                  display={summary.current.display}
                  benchmark={1}
                  benchmarkLabel="1.0"
                  max={3}
                />
              </div>
            )}
          </div>
        </Card>
      )}

    <Card title="Reports">
      <Rows>
        {items
          .filter((item) => !limits.hidden.has(item.id))
          .map((item) => (
          <Row key={item.id} title={item.title} sub={item.sub} onClick={() => navigate({ id: item.id })} />
        ))}
      </Rows>
    </Card>
    </div>
  );
}

function Lines({ lines }: { lines: ReportLine[] }) {
  const [open, setOpen] = useState<string | null>(null);
  if (lines.length === 0) return <Empty>Nothing here.</Empty>;

  return (
    <Rows>
      {lines.map((line) => (
        <li key={line.name}>
          <button
            type="button"
            onClick={() => setOpen(open === line.name ? null : line.name)}
            className="flex w-full items-center gap-3 py-3 text-left"
          >
            <span className="min-w-0 flex-1 truncate text-sm">{line.name}</span>
            <span className="shrink-0 text-sm tabular">{formatMoney(line.amount)}</span>
          </button>
          {open === line.name && line.children && (
            <ul className="space-y-1 pb-2 pl-3">
              {line.children.map((child) => (
                <li key={child.name} className="flex justify-between gap-3 text-xs text-ink-2">
                  <span className="truncate">{child.name}</span>
                  <span className="shrink-0 tabular">{formatMoney(child.amount)}</span>
                </li>
              ))}
            </ul>
          )}
        </li>
      ))}
    </Rows>
  );
}

export function BalanceSheet() {
  const { snapshot, groups } = useApp();
  const model = useMemo(
    () => (snapshot && groups ? deriveBalanceSheet(snapshot, groups) : null),
    [snapshot, groups],
  );
  if (!model) return <Empty>Nothing loaded.</Empty>;

  return (
    <div className="space-y-4">
      {/*
        The shape of the business in one line, before the detail. Assets against
        liabilities is a genuine two-sided comparison, so it gets the opposing
        bar rather than two separate charts the reader has to hold in their head.
      */}
      <Card title="The shape of it">
        <OpposingBar
          left={model.totalAssets}
          right={model.totalLiabilities}
          leftLabel="Assets"
          rightLabel="Liabilities"
        />
        <div className="mt-4 grid gap-4 sm:grid-cols-2">
          <div>
            <div className="mb-2 text-[11px] font-bold uppercase tracking-[0.08em] text-ink-3">
              Biggest assets
            </div>
            <RankedBars data={model.assets.map((l) => ({ label: l.name, value: l.amount }))} />
          </div>
          <div>
            <div className="mb-2 text-[11px] font-bold uppercase tracking-[0.08em] text-ink-3">
              Biggest liabilities
            </div>
            <RankedBars
              data={model.liabilities.map((l) => ({ label: l.name, value: l.amount }))}
              tone="var(--accent-2)"
            />
          </div>
        </div>
      </Card>

      <Card title={`Assets — ${formatMoney(model.totalAssets)}`}>
        <Lines lines={model.assets} />
      </Card>
      <Card title={`Liabilities — ${formatMoney(model.totalLiabilities)}`}>
        <Lines lines={model.liabilities} />
      </Card>
      <Card title="Closing the books">
        <div className="space-y-1 text-sm">
          <div className="flex justify-between">
            <span className="text-ink-2">Profit for the period</span>
            <span className="tabular">{formatMoney(model.netProfit)}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-ink-2">Unexplained difference</span>
            <span className={`tabular ${Math.abs(model.difference) >= 1 ? "text-bad" : ""}`}>
              {formatMoney(model.difference)}
            </span>
          </div>
        </div>
        {Math.abs(model.difference) >= 1 && (
          // Naming the gap is better than presenting a balance sheet that
          // silently does not balance.
          <p className="mt-2 text-xs text-bad">
            Assets minus liabilities does not equal the period's profit. The snapshot's date range
            probably excludes some opening entries.
          </p>
        )}
      </Card>
    </div>
  );
}

export function ProfitLoss() {
  const { snapshot, groups } = useApp();
  const model = useMemo(
    () => (snapshot && groups ? deriveProfitLoss(snapshot, groups) : null),
    [snapshot, groups],
  );
  if (!model) return <Empty>Nothing loaded.</Empty>;

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-3">
        <Card title="Where the money came from, and went">
          <OpposingBar
            left={model.totalIncome}
            right={model.totalExpenses}
            leftLabel="Income"
            rightLabel="Expenses"
          />
          <div className="mt-4 grid gap-4 sm:grid-cols-2">
            <div>
              <div className="mb-2 text-[11px] font-bold uppercase tracking-[0.08em] text-ink-3">
                Income
              </div>
              <RankedBars data={model.income.map((l) => ({ label: l.name, value: l.amount }))} />
            </div>
            <div>
              <div className="mb-2 text-[11px] font-bold uppercase tracking-[0.08em] text-ink-3">
                Expenses
              </div>
              <RankedBars
                data={model.expenses.map((l) => ({ label: l.name, value: l.amount }))}
                tone="var(--accent-2)"
              />
            </div>
          </div>
        </Card>

        <Card title="Gross profit">
          <span className="text-xl font-semibold tabular">{formatMoney(model.grossProfit)}</span>
        </Card>
        <Card title="Net profit">
          <span
            className={`text-xl font-semibold tabular ${
              model.netProfit < 0 ? "text-bad" : "text-good"
            }`}
          >
            {formatMoney(model.netProfit)}
          </span>
        </Card>
      </div>
      <Card title={`Income — ${formatMoney(model.totalIncome)}`}>
        <Lines lines={model.income} />
      </Card>
      <Card title={`Expenses — ${formatMoney(model.totalExpenses)}`}>
        <Lines lines={model.expenses} />
      </Card>
    </div>
  );
}

export function TrialBalance() {
  const { snapshot, groups } = useApp();
  const model = useMemo(
    () => (snapshot && groups ? deriveTrialBalance(snapshot, groups) : null),
    [snapshot, groups],
  );
  if (!model) return <Empty>Nothing loaded.</Empty>;

  return (
    <div className="space-y-4">
    {/*
      Two hundred rows hide the shape completely. The largest balances on each
      side answer "what is this company mostly made of" before the table
      answers "and what is every line of it".
    */}
    <Card title="Largest balances">
      <div className="grid gap-4 sm:grid-cols-2">
        <div>
          <div className="mb-2 text-[11px] font-bold uppercase tracking-[0.08em] text-ink-3">
            Debit
          </div>
          <RankedBars
            data={model.rows.filter((r) => r.debit > 0).map((r) => ({ label: r.ledger, value: r.debit }))}
            limit={5}
          />
        </div>
        <div>
          <div className="mb-2 text-[11px] font-bold uppercase tracking-[0.08em] text-ink-3">
            Credit
          </div>
          <RankedBars
            data={model.rows.filter((r) => r.credit > 0).map((r) => ({ label: r.ledger, value: r.credit }))}
            limit={5}
            tone="var(--accent-2)"
          />
        </div>
      </div>
    </Card>

    <ShareBar
      csv={[
        {
          label: "Export CSV",
          filename: "trial-balance",
          build: () => [
            ["Ledger", "Group", "Debit", "Credit"],
            ...model.rows.map((row) => [
              row.ledger,
              row.group,
              row.debit ? row.debit.toFixed(2) : "",
              row.credit ? row.credit.toFixed(2) : "",
            ]),
            // The totals row is what gets compared against Tally's own screen;
            // leaving it to the reader to re-sum 200 rows invites a mistake in
            // the one place the whole check depends on.
            ["TOTAL", "", model.totalDebit.toFixed(2), model.totalCredit.toFixed(2)],
          ],
        },
      ]}
    />
    <Card
      title={`${model.rows.length} ledgers`}
      action={
        <span className={`text-xs ${Math.abs(model.difference) >= 0.01 ? "text-bad" : "text-good"}`}>
          {Math.abs(model.difference) < 0.01 ? "balanced" : `out by ${formatMoney(model.difference)}`}
        </span>
      }
    >
      <Scroller>
        <table className="w-full min-w-[30rem] text-sm">
          <thead>
            <tr className="text-left text-xs uppercase tracking-wide text-ink-3">
              <th className="py-2 pr-3 font-medium">Ledger</th>
              <th className="py-2 pr-3 text-right font-medium">Debit</th>
              <th className="py-2 text-right font-medium">Credit</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-line">
            {model.rows.map((row) => (
              <tr key={row.ledger}>
                <td className="py-2 pr-3">
                  <div className="truncate text-ink">{row.ledger}</div>
                  <div className="truncate text-xs text-ink-3">{row.group}</div>
                </td>
                <td className="py-2 pr-3 text-right tabular">
                  {row.debit ? formatMoney(row.debit) : ""}
                </td>
                <td className="py-2 text-right tabular">
                  {row.credit ? formatMoney(row.credit) : ""}
                </td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr className="border-t border-line font-medium">
              <td className="py-2 pr-3">Total</td>
              <td className="py-2 pr-3 text-right tabular">{formatMoney(model.totalDebit)}</td>
              <td className="py-2 text-right tabular">{formatMoney(model.totalCredit)}</td>
            </tr>
          </tfoot>
        </table>
      </Scroller>
    </Card>
    </div>
  );
}

export function CashBook() {
  const { snapshot, groups } = useApp();
  const [selected, setSelected] = useState<string | null>(null);

  const accounts = useMemo(
    () => (snapshot && groups ? deriveCashAccounts(snapshot, groups) : []),
    [snapshot, groups],
  );
  const statement = useMemo(
    () => (snapshot && selected ? deriveLedger(snapshot, selected) : null),
    [snapshot, selected],
  );

  if (!snapshot) return <Empty>Nothing loaded.</Empty>;
  if (accounts.length === 0) return <Empty>No cash or bank ledgers found.</Empty>;

  return (
    <div className="space-y-4">
      <Card title="Where the money sits">
        <RankedBars
          data={accounts.map((a) => ({ label: a.ledger, value: a.balance }))}
          limit={8}
        />
      </Card>

      <Card title="Accounts">
        <Rows>
          {accounts.map((account) => (
            <Row
              key={account.ledger}
              title={account.ledger}
              sub={account.kind}
              right={<Money amount={account.balance} />}
              onClick={() => setSelected(selected === account.ledger ? null : account.ledger)}
            />
          ))}
        </Rows>
      </Card>

      {statement && (
        <Card title={`${statement.ledger} — ${statement.lines.length} movements`}>
          <Rows>
            {statement.lines
              .slice()
              .reverse()
              .slice(0, 200)
              .map((line, i) => (
                <Row
                  key={`${line.voucherId}-${i}`}
                  title={line.counterparty || line.voucherType}
                  sub={`${line.voucherType} ${line.voucherNumber} · ${formatDate(line.date)}`}
                  // A debit to cash is money in; showing it as a plain +/- is
                  // clearer here than Dr/Cr.
                  right={<Money amount={-line.amount} signed />}
                  rightSub={`bal ${formatMoney(Math.abs(line.balance))}`}
                />
              ))}
          </Rows>
        </Card>
      )}
    </div>
  );
}

export function Ratios() {
  const { snapshot, groups, asOf } = useApp();
  const ratios = useMemo(
    () => (snapshot && groups ? deriveRatios(snapshot, asOf, groups) : []),
    [snapshot, groups, asOf],
  );
  if (ratios.length === 0) return <Empty>Nothing loaded.</Empty>;

  return (
    <Card title="Ratios">
      <ul className="space-y-5">
        {ratios.map((ratio) => {
          const mark = BENCHMARKS[ratio.name];
          return (
            <li key={ratio.name}>
              <div className="mb-1.5">
                <div className="text-[15px] text-ink">{ratio.name}</div>
                <div className="text-xs text-ink-3">{ratio.hint}</div>
              </div>
              <RatioBar
                value={ratio.value}
                display={ratio.display}
                {...(mark ?? {})}
              />
            </li>
          );
        })}
      </ul>
    </Card>
  );
}

/**
 * Reference levels, for the three ratios that HAVE a conventional one.
 *
 * Liquidity ratios are judged against 1.0 — below it, short-term liabilities
 * exceed the assets available to meet them, and that is a textbook line rather
 * than our opinion. Everything else here (margins, debt to equity, receivables
 * against sales) varies so much by trade that any number we drew would be an
 * invention, so those get a plain bar and no verdict.
 */
const BENCHMARKS: Record<
  string,
  { benchmark: number; benchmarkLabel: string; higherIsBetter: boolean; max?: number }
> = {
  "Current ratio": { benchmark: 1, benchmarkLabel: "1.0", higherIsBetter: true, max: 3 },
  "Quick ratio": { benchmark: 1, benchmarkLabel: "1.0", higherIsBetter: true, max: 3 },
};
